import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowLeft, Handshake, Coins, Briefcase, Users, HeartHandshake, GraduationCap, Building2, User } from "lucide-react";
import { RegisterSoonButton } from "@/components/ui-iyaf/RegisterSoonButton";

const orgLaneDefs = [
  { laneKey: "strategic", icon: Handshake, color: "#3663A8", testid: "lane-strategic" },
  { laneKey: "sponsorship", icon: Coins, color: "#FF9F1C", testid: "lane-sponsorship" },
  { laneKey: "consult", icon: Briefcase, color: "#8C8C8C", testid: "lane-consult" },
];

const indLaneDefs = [
  { laneKey: "programs", icon: GraduationCap, color: "#E01E84", testid: "lane-programs" },
  { laneKey: "volunteer", icon: Users, color: "#3663A8", testid: "lane-volunteer" },
  { laneKey: "donate", icon: HeartHandshake, color: "#FF9F1C", testid: "lane-donate" },
];

function Lane({ icon: Icon, color, title, desc, testid, t }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5 }}
      className="group relative p-8 sm:p-10 rounded-3xl bg-white border border-black/5 hover:-translate-y-1 transition-all overflow-hidden"
      data-testid={testid}
    >
      <div className="absolute -right-16 -top-16 w-44 h-44 rounded-full opacity-[0.08] group-hover:opacity-100 transition" style={{ background: color }} />
      <div className="relative">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ background: `${color}1A`, color }}>
          <Icon size={24} />
        </div>
        <h3 className="font-display text-2xl font-bold mb-3">{title}</h3>
        <p className="text-iyaf-ink/70 leading-relaxed">{desc}</p>
        <Link to="/contact" className="mt-6 inline-flex items-center gap-2 font-semibold text-sm" style={{ color }}>
          {t("cta.getInTouch")} <ArrowRight size={15} />
        </Link>
      </div>
    </motion.div>
  );
}

function AudienceCard({ k, onSelect, t, audiences }) {
  const a = audiences[k];
  const Icon = a.icon;
  return (
    <motion.button
      onClick={() => onSelect(k)}
      data-testid={`audience-${k}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: k === "organisation" ? 0 : 0.08 }}
      whileHover={{ y: -4 }}
      className="group relative text-left p-10 sm:p-12 rounded-[32px] bg-white border border-black/5 overflow-hidden transition-shadow hover:shadow-2xl hover:shadow-black/10"
    >
      <div
        className="absolute -right-20 -top-20 w-72 h-72 rounded-full opacity-[0.08] group-hover:opacity-100 group-hover:scale-110 transition-all duration-500"
        style={{ background: a.accent }}
      />
      <div className="relative">
        <div className="flex items-center gap-3 mb-8">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ background: a.accent, color: "#fff" }}
          >
            <Icon size={28} strokeWidth={2} />
          </div>
        </div>
        <h3 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.05] mb-3">
          {a.label}
        </h3>
        <p className="text-iyaf-ink/65 leading-relaxed text-[15px] mb-8 max-w-md">
          {a.blurb}
        </p>
        <span
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white transition-transform group-hover:translate-x-1"
          style={{ background: a.accent }}
        >
          {t("engage.seeMyLanes")} <ArrowRight size={15} />
        </span>
      </div>
    </motion.button>
  );
}

export default function Engage() {
  const { t } = useTranslation();
  const [audience, setAudience] = useState(null);

  const audiences = {
    organisation: {
      label: t("engage.organisation"),
      eyebrow: t("engage.orgEyebrow"),
      eyebrowColor: "text-iyaf-blue",
      headline: t("engage.orgHeadline"),
      laneDefs: orgLaneDefs,
      accent: "#3663A8",
      icon: Building2,
      blurb: t("engage.orgBlurb"),
    },
    individual: {
      label: t("engage.individual"),
      eyebrow: t("engage.indEyebrow"),
      eyebrowColor: "text-iyaf-magenta",
      headline: t("engage.indHeadline"),
      laneDefs: indLaneDefs,
      accent: "#E01E84",
      icon: User,
      blurb: t("engage.indBlurb"),
    },
  };

  return (
    <div className="relative">
      <section className="pt-16 pb-10">
        <div className="container-iyaf">
          <p className="eyebrow text-iyaf-orange mb-5">{t("engage.eyebrow")}</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95] max-w-4xl text-balance">
            {t("engage.titleLine1")}<br /> <span className="font-editorial italic font-normal text-iyaf-orange">{t("engage.titleLine2")}</span>
          </h1>
          <p className="mt-8 font-editorial text-2xl text-iyaf-ink/75 leading-snug max-w-3xl">
            {t("engage.lead")}
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="container-iyaf">
          <AnimatePresence mode="wait">
            {!audience ? (
              <motion.div
                key="picker"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                data-testid="audience-picker"
              >
                <div className="text-center mb-10">
                  <p className="eyebrow text-iyaf-ink/55 mb-3">{t("engage.pickerEyebrow")}</p>
                  <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
                    {t("engage.pickerTitle")}
                  </h2>
                  <p className="mt-4 text-iyaf-ink/65 max-w-xl mx-auto">
                    {t("engage.pickerBody")}
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                  <AudienceCard k="organisation" onSelect={setAudience} t={t} audiences={audiences} />
                  <AudienceCard k="individual" onSelect={setAudience} t={t} audiences={audiences} />
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={`lanes-${audience}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
                data-testid={`lanes-${audience}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
                  <div>
                    <p className={`eyebrow ${audiences[audience].eyebrowColor} mb-3`}>
                      {audiences[audience].eyebrow}
                    </p>
                    <h2 className="h2 text-balance">{audiences[audience].headline}</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setAudience(audience === "organisation" ? "individual" : "organisation")}
                      data-testid="audience-switch"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-black/10 text-xs font-semibold text-iyaf-ink/75 hover:text-iyaf-ink hover:bg-iyaf-paper transition"
                    >
                      {t("engage.switchTo", { audience: audience === "organisation" ? t("engage.individual").toLowerCase() : t("engage.organisation").toLowerCase() })}
                    </button>
                    <button
                      onClick={() => setAudience(null)}
                      data-testid="audience-back"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-iyaf-ink/60 hover:text-iyaf-ink transition"
                    >
                      <ArrowLeft size={13} /> {t("cta.back")}
                    </button>
                  </div>
                </div>
                <div className="grid lg:grid-cols-3 gap-6">
                  {audiences[audience].laneDefs.map((l) => (
                    <Lane
                      key={l.laneKey}
                      icon={l.icon}
                      color={l.color}
                      title={t(`engage.lanes.${l.laneKey}.title`)}
                      desc={t(`engage.lanes.${l.laneKey}.desc`)}
                      testid={l.testid}
                      t={t}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <section className="py-20">
        <div className="container-iyaf">
          <div className="rounded-[40px] bg-iyaf-ink text-white p-10 sm:p-16 text-center relative overflow-hidden">
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-40">
              <div className="w-[500px] h-[500px] rounded-full border border-white/10 animate-spin-slow" />
            </div>
            <p className="eyebrow text-iyaf-orange mb-5 relative">{t("engage.ctaEyebrow")}</p>
            <h2 className="relative font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-3xl mx-auto text-balance">
              {t("engage.ctaTitle")}
            </h2>
            <div className="relative mt-10">
              <RegisterSoonButton
                label={t("cta.beginRegistration")}
                icon={ArrowRight}
                testid="engage-cta-register"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-iyaf-ink font-semibold hover:bg-iyaf-orange transition"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
